import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setSession(data);
      nav("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <h2 className="text-2xl font-bold tracking-tight text-center">Create your account</h2>
        <p className="text-muted-foreground text-sm mt-1 mb-6 text-center">Start your loan application in minutes.</p>

        {err && <div data-testid="register-error" className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">{err}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="register-name-input" required value={form.name} onChange={set("name")} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="phone">Phone number</Label>
            <Input id="phone" data-testid="register-phone-input" type="tel" inputMode="numeric" maxLength={10} required value={form.phone} onChange={set("phone")} className="mt-1.5" placeholder="10-digit mobile" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="register-password-input" type="password" required value={form.password} onChange={set("password")} className="mt-1.5" placeholder="Min 6 characters" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11" data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Account
          </Button>
        </form>

        <p className="text-sm text-center text-muted-foreground mt-6">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-brand hover:underline">Log In</Link>
        </p>
      </div>
    </div>
  );
}
